// Demos dos cards da aba Estilo — porta 1:1 de buildKaraokeDemo / buildStackedDemo /
// buildScatterDemo / buildStaticDemo / buildHeadlineDemo do edvid (app.js ~205–585).
// Cada número vem do render (Main.tsx, StackedCaptions.tsx, ScatterCaptions.tsx,
// SimpleCaptions.tsx) escalado por largura/1080: o card mostra a proporção, a face e
// o movimento reais. Imperativo de propósito (DOM + rAF), como no original — são
// dezenas de spans animados por quadro, e passar isso por estado do React não se paga.
import { useEffect, useLayoutEffect, useRef } from 'react';
import type { CaptionStyleId, HeadlineStyleId } from '../../../types/videoEdit';

const CAP_TEXT = 'É assim que sua legenda irá aparecer';
const HEADLINE_TEXT = 'É assim que vai ficar a sua headline';
const FPS_REF = 30;

type Step = (now: number) => void;

function bez(x1: number, y1: number, x2: number, y2: number) {
  const cx = 3 * x1; const bx = 3 * (x2 - x1) - cx; const ax = 1 - cx - bx;
  const cy = 3 * y1; const by = 3 * (y2 - y1) - cy; const ay = 1 - cy - by;
  const fx = (t: number) => ((ax * t + bx) * t + cx) * t;
  const dfx = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  return (x: number) => {
    let t = x;
    for (let i = 0; i < 6; i++) {
      const e = fx(t) - x;
      if (Math.abs(e) < 1e-4) break;
      const d = dfx(t);
      if (Math.abs(d) < 1e-6) break;
      t -= e / d;
    }
    return ((ay * t + by) * t + cy) * t;
  };
}
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
const easeStack = bez(0.16, 1, 0.3, 1);
const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, parent?: HTMLElement) {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (parent) parent.appendChild(e);
  return e;
}

// ── karaoke: linhas de ≤3 palavras, Poppins 900, cada palavra sobe 34px em 7 quadros
function buildKaraoke(host: HTMLElement): Step {
  const s = host.clientWidth / 1080;
  const wrap = el('div', 'edv-cap-demo', host);
  const words = CAP_TEXT.split(' ');
  const lines: string[][] = [];
  for (let i = 0; i < words.length; i += 3) lines.push(words.slice(i, i + 3));
  const STEP = 0.26; const ENTER = 7 / FPS_REF; const HOLD = 0.6;
  const rise = 34 * s;
  const built: Array<{ box: HTMLElement; spans: HTMLElement[]; start: number; end: number }> = [];
  let t = 0;
  for (const ln of lines) {
    const box = el('div', 'edv-kar-line', wrap);
    box.style.fontSize = `${61 * s}px`;
    const spans = ln.map(w => { const sp = el('span', '', box); sp.textContent = w; sp.style.marginRight = `${18 * s}px`; return sp; });
    const start = t;
    t = start + (ln.length - 1) * STEP + ENTER + HOLD;
    built.push({ box, spans, start, end: t });
  }
  const cycle = t + 0.3;
  return now => {
    const p = now % cycle;
    for (const L of built) {
      const on = p >= L.start && p < L.end;
      L.box.style.display = on ? '' : 'none';
      if (!on) continue;
      L.spans.forEach((sp, j) => {
        const e = easeOutCubic(clamp01((p - (L.start + j * STEP)) / ENTER));
        sp.style.opacity = String(e);
        sp.style.translate = `0px ${((1 - e) * rise).toFixed(2)}px`;
      });
    }
  };
}

// ── stacked: um cue, linhas no ciclo STACK_MIXED; palavras sobem 46px saindo do blur
const STK_LINES = [
  { words: ['É', 'assim'], style: 0 },
  { words: ['que', 'sua', 'legenda'], style: 1 },
  { words: ['irá', 'aparecer'], style: 2 },
];
function buildStacked(host: HTMLElement): Step {
  const s = host.clientWidth / 1080;
  const wrap = el('div', 'edv-cap-demo', host);
  const cue = el('div', 'edv-stk-cue', wrap);
  const STEP = 0.2; const ENTER = 8 / FPS_REF; const HOLD = 0.8; const EXIT = 7 / FPS_REF;
  const rise = 46 * s; const blurIn = 5 * s; const upY = 55 * s; const cueBlur = 14 * s;
  const shadow = `drop-shadow(0 ${(5 * s).toFixed(2)}px ${(9 * s).toFixed(2)}px rgba(0,0,0,0.5))`;
  const all: Array<{ sp: HTMLElement; start: number }> = [];
  let idx = 0;
  for (const L of STK_LINES) {
    const row = el('div', 'edv-stk-line', cue);
    let size = 69;
    if (L.style === 1) size = Math.round(size * 0.72);
    if (L.style === 2) size = Math.round(size * 0.95);
    row.style.fontSize = `${size * s}px`;
    L.words.forEach((w, i) => {
      const sp = el('span', `s${L.style}`, row);
      sp.textContent = w + (i < L.words.length - 1 ? ' ' : '');
      all.push({ sp, start: idx * STEP });
      idx++;
    });
  }
  const exitStart = (idx - 1) * STEP + ENTER + HOLD;
  const cycle = exitStart + EXIT + 0.15;
  return now => {
    const p = now % cycle;
    const ex = clamp01((p - exitStart) / EXIT);
    cue.style.opacity = String(1 - ex);
    cue.style.translate = `0px ${(-upY * ex).toFixed(2)}px`;
    cue.style.filter = ex > 0.02 ? `blur(${(cueBlur * ex).toFixed(2)}px)` : '';
    for (const w of all) {
      const e = easeStack(clamp01((p - w.start) / ENTER));
      const eb = (1 - e) * blurIn;
      w.sp.style.opacity = String(e);
      w.sp.style.translate = `0px ${((1 - e) * rise).toFixed(2)}px`;
      w.sp.style.filter = `${eb > 0.06 ? `blur(${eb.toFixed(2)}px) ` : ''}${shadow}`;
    }
  };
}

// ── scatter: serifa, minúsculas, uma palavra por vez; a palavra-destaque sai do blur
const SCAT = { base: 58, hiScale: 1.62, gap: 12, spread: 0.45, safeW: 820 };
const scatHash = (n: number) => { const x = Math.sin(n * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x); };
function buildScatter(host: HTMLElement): Step {
  const s = host.clientWidth / 1080;
  const wrap = el('div', 'edv-cap-demo', host);
  const cue = el('div', 'edv-scat-cue', wrap);
  const words = CAP_TEXT.toLowerCase().split(' ');
  let hiIdx = -1; let hiLen = 6;
  words.forEach((w, i) => { if (w.length > hiLen) { hiLen = w.length; hiIdx = i; } });
  type It = { w: string; i: number; hi: boolean };
  const lines: It[][] = [];
  let line: It[] = [];
  words.forEach((w, i) => {
    if (i === hiIdx) { if (line.length) lines.push(line); lines.push([{ w, i, hi: true }]); line = []; return; }
    line.push({ w, i, hi: false });
    if (line.length >= (scatHash(31 + i) > 0.5 ? 4 : 3)) { lines.push(line); line = []; }
  });
  if (line.length) lines.push(line);
  const STEP = 0.22; const ENTER = 7 / FPS_REF; const HI_ENTER = 10 / FPS_REF; const HOLD = 0.9; const EXIT = 8 / FPS_REF;
  const all: Array<{ sp: HTMLElement; start: number; hi: boolean }> = [];
  lines.forEach((ln, li) => {
    const row = el('div', 'edv-scat-line', cue);
    row.style.gap = `${SCAT.gap * s}px`;
    let w = 0;
    for (const it of ln) {
      const sp = el('span', it.hi ? 'hi' : '', row);
      sp.textContent = it.w;
      sp.style.fontSize = `${(it.hi ? SCAT.base * SCAT.hiScale : SCAT.base) * s}px`;
      all.push({ sp, start: it.i * STEP, hi: it.hi });
      w += sp.offsetWidth + SCAT.gap * s;
    }
    const room = Math.max(0, (SCAT.safeW * s - w) / 2) * SCAT.spread;
    row.style.translate = `${((scatHash(17 + li * 5 + 3) * 2 - 1) * room).toFixed(1)}px 0px`;
  });
  const exitStart = (words.length - 1) * STEP + ENTER + HOLD;
  const cycle = exitStart + EXIT + 0.35;
  const blurIn = 26 * s; const blurOut = 30 * s;
  return now => {
    const p = now % cycle;
    const out = clamp01((p - exitStart) / EXIT);
    for (const w of all) {
      const t = easeOutCubic(clamp01((p - w.start) / (w.hi ? HI_ENTER : ENTER)));
      w.sp.style.opacity = String(t * (1 - out));
      if (w.hi) {
        const b = (1 - t) * blurIn + out * blurOut;
        w.sp.style.filter = b > 0.1 ? `blur(${b.toFixed(2)}px)` : '';
      }
    }
  };
}

// ── medição tipográfica em unidades do render (1080 de largura)
let meter: HTMLSpanElement | null = null;
function measureType(text: string, size: number, weight: number, family?: string, tracking?: number) {
  if (!text) return 0;
  if (!meter) {
    meter = document.createElement('span');
    meter.style.cssText = 'position:absolute;left:-9999px;top:0;visibility:hidden;white-space:pre;';
    document.body.appendChild(meter);
  }
  meter.style.fontFamily = family || "'Poppins',sans-serif";
  meter.style.letterSpacing = `${tracking == null ? -1 : tracking}px`;
  meter.style.fontSize = `${size}px`;
  meter.style.fontWeight = String(weight);
  meter.textContent = text;
  return meter.offsetWidth;
}

// ── os três estilos ESTÁTICOS: cues por largura MEDIDA, limitados a maxWords
const STATIC_VARIANTS = {
  simples: { family: "'Poppins',sans-serif", weight: 600, size: 66, maxWords: 3, lines: 1, sx: 0.9, sy: 0.9, tracking: -3, maxW: 860 },
  serifada: { family: "'Libre Baskerville',serif", weight: 700, size: 67, maxWords: 3, lines: 1, sx: 1, sy: 1, tracking: -1, maxW: 860 },
  classica: { family: "'Inter',sans-serif", weight: 500, size: 42, maxWords: 14, lines: 2, sx: 1, sy: 1, tracking: 0, maxW: 840 },
} as const;
const ORPHAN_PT = /^(o|a|os|as|e|é|de|do|da|em|no|na|um|uma|que|se|ao|à|por|com)$/i;

function buildStatic(host: HTMLElement, id: keyof typeof STATIC_VARIANTS): Step | null {
  const V = STATIC_VARIANTS[id];
  const s = host.clientWidth / 1080;
  const wrap = el('div', 'edv-cap-demo', host);
  const words = CAP_TEXT.split(' ');
  const wOf = (ws: string[]) => measureType(ws.join(' '), V.size, V.weight, V.family, V.tracking) * V.sx;
  const cues: string[][] = [];
  let cur: string[] = [];
  for (const w of words) {
    const trial = [...cur, w];
    if (cur.length && (trial.length > V.maxWords || wOf(trial) > V.maxW * V.lines)) { cues.push(cur); cur = [w]; } else cur = trial;
  }
  if (cur.length) cues.push(cur);
  const boxes = cues.map(cue => {
    let lines = [cue];
    if (V.lines === 2 && cue.length > 1) {
      let best = 1; let bestScore = Infinity;
      for (let i = 1; i < cue.length; i++) {
        const score = Math.abs(wOf(cue.slice(0, i)) - wOf(cue.slice(i))) + (ORPHAN_PT.test(cue[i - 1]) ? 200 : 0);
        if (score < bestScore) { bestScore = score; best = i; }
      }
      lines = [cue.slice(0, best), cue.slice(best)];
    }
    const box = el('div', 'edv-stat-demo', wrap);
    box.style.fontFamily = V.family;
    box.style.fontWeight = String(V.weight);
    box.style.fontSize = `${V.size * s}px`;
    box.style.letterSpacing = `${V.tracking * s}px`;
    box.style.transform = V.sx === 1 && V.sy === 1 ? '' : `scale(${V.sx}, ${V.sy})`;
    for (const ln of lines) el('div', '', box).textContent = ln.join(' ');
    return box;
  });
  if (boxes.length < 2) return null;
  const HOLD = 0.95;
  const cycle = boxes.length * HOLD;
  return now => {
    const i = Math.floor((now % cycle) / HOLD);
    boxes.forEach((b, k) => { b.style.display = k === i ? '' : 'none'; });
  };
}

// ── headline: quebra em 2 linhas por largura medida + ajuste ao safeW (HookInner)
const HL_MIN = 28;
const HL_STYLES = {
  outline: { weights: [800, 800], cap: 51, safeW: 900, lh: 1.02 },
  card: { weights: [900, 900], cap: 46, safeW: 820, lh: 1.06 },
  realce: { weights: [900, 900], cap: 48, safeW: 830, lh: 1.04 },
  misto: { weights: [400, 900], cap: 55, safeW: 900, lh: 0.98 },
} as const;
type HlId = keyof typeof HL_STYLES;

function hlTwoLines(text: string, weights: readonly number[]): [string, string] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return [words[0] || '', ''];
  let best: [string, string] = [words[0], words.slice(1).join(' ')];
  let bestDiff = Infinity;
  for (let i = 1; i < words.length; i++) {
    const a = words.slice(0, i).join(' ');
    const b = words.slice(i).join(' ');
    const d = Math.abs(measureType(a, 100, weights[0]) - measureType(b, 100, weights[1]));
    if (d < bestDiff) { bestDiff = d; best = [a, b]; }
  }
  return best;
}

function buildHeadline(host: HTMLElement, styleId: HlId, text = HEADLINE_TEXT) {
  const s = host.clientWidth / 1080;
  const S = HL_STYLES[styleId];
  const wrap = el('div', 'edv-cap-demo', host);
  const raw = styleId === 'card' ? text.toUpperCase() : text;
  const lines = hlTwoLines(raw, S.weights);
  const widest = (size: number) => Math.max(measureType(lines[0], size, S.weights[0]), measureType(lines[1], size, S.weights[1]));
  let fit = Math.floor((S.safeW / Math.max(1, widest(100))) * 100);
  fit = Math.floor((S.safeW / Math.max(1, widest(fit))) * fit);
  const size = Math.max(HL_MIN, Math.min(fit, S.cap)) * s;
  const box = el('div', `edv-hl-demo edv-hl-${styleId}`, wrap);
  box.style.lineHeight = String(S.lh);
  box.style.letterSpacing = `${-1 * s}px`;
  if (styleId === 'realce') {
    for (const l of lines) {
      if (!l) continue;
      const b = el('div', 'edv-hl-block', box);
      b.style.fontSize = `${size}px`;
      b.style.borderRadius = `${12 * s}px`;
      b.textContent = l;
    }
    return;
  }
  if (styleId === 'card') { box.style.borderRadius = `${24 * s}px`; box.style.padding = `${28 * s}px ${46 * s}px`; }
  if (styleId === 'outline') (box.style as any).webkitTextStroke = `${7 * s}px #000`;
  lines.forEach((l, i) => {
    if (!l) return;
    const d = el('div', '', box);
    d.style.fontSize = `${size}px`;
    d.style.fontWeight = String(S.weights[i]);
    if (styleId === 'misto') d.style.color = i === 1 ? 'var(--hl-accent)' : '#fff';
    d.textContent = l;
  });
}

// ── laço único de animação para todos os cards visíveis
const steps = new Set<Step>();
let rafId = 0;
function tick() {
  const now = performance.now() / 1000;
  steps.forEach(fn => fn(now));
  rafId = steps.size ? requestAnimationFrame(tick) : 0;
}
function register(fn: Step) {
  steps.add(fn);
  if (!rafId) rafId = requestAnimationFrame(tick);
  return () => { steps.delete(fn); };
}

export function CaptionDemo({ id, fontsVersion }: { id: Exclude<CaptionStyleId, 'none'>; fontsVersion: number }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const host = ref.current;
    if (!host) return;
    host.innerHTML = '';
    const step = id === 'karaoke' ? buildKaraoke(host)
      : id === 'stacked' ? buildStacked(host)
      : id === 'scatter' ? buildScatter(host)
      : buildStatic(host, id);
    if (!step) return;
    return register(step);
  }, [id, fontsVersion]);
  return <div ref={ref} className="relative h-full w-full overflow-hidden" />;
}

/** Miniaturas dos dois estilos novos (mesmas fontes/proporções da composição). */
function StackedHeadlineDemo() {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center leading-[0.98]" style={{ filter: 'drop-shadow(0 2px 5px rgba(0,0,0,.5))' }}>
      <span style={{ fontFamily: 'Poppins', fontWeight: 900, fontStyle: 'italic', fontSize: 17, color: '#fff', letterSpacing: -0.5 }}>sua máquina</span>
      <span style={{ fontFamily: 'Poppins', fontWeight: 400, fontSize: 11, color: '#fff' }}>pode estar perdendo</span>
      <span style={{ fontFamily: '"Playfair Display"', fontWeight: 900, fontStyle: 'italic', fontSize: 20, color: 'var(--hl-accent)', marginTop: -2 }}>um tempão</span>
    </div>
  );
}

function QuestionHeadlineDemo() {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="w-[104px] overflow-hidden rounded-[5px] text-center shadow-lg" style={{ fontFamily: 'Poppins' }}>
        <div className="bg-[#1C1C1E] px-1.5 py-1 text-[7px] font-semibold text-white">O que você me perguntaria?</div>
        <div className="bg-white px-1.5 pb-1.5 pt-1 text-[9px] font-medium leading-tight text-[#1C1C1E]">Dá pra vender na Shopee sem estoque?</div>
      </div>
    </div>
  );
}

export function HeadlineDemo({ id, fontsVersion, text }: { id: Exclude<HeadlineStyleId, 'none'>; fontsVersion: number; text?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const host = ref.current;
    if (!host || id === 'empilhado' || id === 'pergunta') return;
    host.innerHTML = '';
    buildHeadline(host, id, text || HEADLINE_TEXT);
  }, [id, fontsVersion, text]);
  if (id === 'empilhado') return <StackedHeadlineDemo />;
  if (id === 'pergunta') return <QuestionHeadlineDemo />;
  return <div ref={ref} className="relative h-full w-full overflow-hidden" />;
}

export const NoneMark = () => (
  <svg viewBox="0 0 64 34" style={{ width: 64, height: 34 }} aria-hidden>
    <rect x="1" y="1" width="62" height="32" rx="5" fill="none" stroke="rgba(255,255,255,.22)" strokeWidth="1.5" strokeDasharray="4 3" />
    <path d="M14 24L50 10" stroke="rgba(255,255,255,.28)" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/** Faces do template (Poppins itálico 900 + Playfair itálico 900 fazem parte do STACK_MIXED). */
const FONTS_HREF = 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,400;0,600;0,700;0,800;0,900;1,900&family=Playfair+Display:ital,wght@1,900&family=Lora:ital,wght@0,400;0,600;1,400;1,600&family=Libre+Baskerville:wght@700&family=Inter:wght@500&display=swap';

/** Carrega as faces e devolve um contador que sobe quando elas chegam (a quebra da headline é MEDIDA). */
export function useDemoFonts(onReady: () => void) {
  useEffect(() => {
    if (!document.querySelector('link[data-edvid-fonts]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = FONTS_HREF;
      link.setAttribute('data-edvid-fonts', '1');
      document.head.appendChild(link);
    }
    let alive = true;
    const fonts = (document as any).fonts as FontFaceSet | undefined;
    if (fonts) {
      Promise.all(['900 20px Poppins', 'italic 900 20px "Playfair Display"', '700 20px "Libre Baskerville"', '400 20px Lora'].map(f => fonts.load(f).catch(() => undefined)))
        .then(() => { if (alive) onReady(); });
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export const STYLE_DEMO_CSS = `
.edv-cap-demo { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; }
.edv-kar-line { position:absolute; display:flex; justify-content:center; align-items:baseline; white-space:nowrap; font-family:'Poppins',sans-serif; font-weight:900; color:#fff; line-height:1; letter-spacing:-1px; text-shadow:0 4px 20px rgba(0,0,0,.55); }
.edv-kar-line span { display:inline-block; will-change:opacity, translate; }
.edv-stk-cue { position:absolute; display:flex; flex-direction:column; align-items:center; text-align:center; }
.edv-stk-line { white-space:pre; letter-spacing:-1.5px; line-height:1.12; }
.edv-stk-line + .edv-stk-line { margin-top:-0.34em; }
.edv-stk-line span { display:inline-block; padding:0 .06em; will-change:opacity, translate, filter; }
.edv-stk-line .s0, .edv-stk-line .s1 { background-image:linear-gradient(180deg,#fff 0%,#fff 46%,#cfcfcf 100%); -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent; color:transparent; }
.edv-stk-line .s0 { font-family:'Poppins',sans-serif; font-weight:900; font-style:italic; }
.edv-stk-line .s1 { font-family:'Poppins',sans-serif; font-weight:400; }
.edv-stk-line .s2 { font-family:'Playfair Display',serif; font-weight:900; font-style:italic; color:var(--hl-accent); }
.edv-scat-cue { position:absolute; display:flex; flex-direction:column; align-items:center; }
.edv-scat-line { display:flex; align-items:baseline; white-space:pre; line-height:1.03; }
.edv-scat-line span { font-family:'Lora',serif; font-weight:400; background-image:linear-gradient(180deg,#f8f5ef 0%,#f4f1e9 54%,#d5cec1 100%); -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent; color:transparent; will-change:opacity, filter; }
.edv-scat-line span.hi { font-weight:600; font-style:italic; }
.edv-stat-demo { position:absolute; text-align:center; color:#f4f1e9; line-height:1.18; white-space:pre; text-shadow:0 1px 5px rgba(0,0,0,.55); }
.edv-hl-demo { position:absolute; text-align:center; font-family:'Poppins',sans-serif; color:#fff; white-space:pre; }
.edv-hl-outline { font-weight:800; paint-order:stroke fill; filter:drop-shadow(0 1px 3px rgba(0,0,0,.45)); }
.edv-hl-card { font-weight:900; background:#232326; box-shadow:0 4px 12px rgba(0,0,0,.45); text-shadow:0 1px 4px rgba(0,0,0,.55); }
.edv-hl-realce { display:flex; flex-direction:column; align-items:center; gap:2px; }
.edv-hl-realce .edv-hl-block { background:var(--hl-accent); color:#fff; font-weight:900; padding:.08em .3em .16em; box-shadow:0 3px 7px rgba(0,0,0,.45); }
.edv-hl-misto { filter:drop-shadow(0 2px 4px rgba(0,0,0,.55)); }
`;
